import { PluginConfigType, PluginsManager } from 'Core/PluginCore/PluginsManager'
import { InputManager } from 'Core/InputManager'
import { TaskManager } from 'Core/TaskManager'
import { Workbench } from 'Core/Workbench'
import {
    ContainerType,
    ContextConfigType,
    NotificationPropsType,
    TaskConfigType,
    UseCaseTypes,
} from 'Types/Tasks.types'
import { ProjectFeatureFlags } from 'HTTPClients/projects.client'
import { BlockEngine } from 'shared/express-interface/blocks-core/BlockEngine'
import { PluginBlockCoordinator } from 'Core/PluginCore/PluginBlockCoordinator'
import { DefaultConnectors } from './PluginCore/DefaultConnectors'
import { BlockComponentRegistry } from 'shared/express-interface/blocks-core/BlockComponentRegistry'
import { builtInBlockComponents } from 'src/core/BlockComponents'
import { BlockComponentProps } from 'shared/express-interface/core/types'

export type StudioConfigType = {
    taskType: UseCaseTypes
    interfaceKey?: string
    featureFlags?: ProjectFeatureFlags['feature_flags']
    taskConfig: unknown
    pluginsConfig: PluginConfigType[]
    context: ContextConfigType
    onNextTask: (notifications?: NotificationPropsType) => void
    onExitTask: (notifications?: NotificationPropsType) => void
}

export class Studio {
    public readonly workbench: Workbench
    public readonly taskManager: TaskManager
    public readonly inputManager: InputManager
    public readonly blockEngine: BlockEngine
    public readonly pluginManager: PluginsManager
    private readonly pluginBlockCoordinator: PluginBlockCoordinator

    constructor(public studioConfig: StudioConfigType) {
        this.workbench = new Workbench(this)
        this.blockEngine = new BlockEngine()
        this.registerContributorBlocks()
        this.taskManager = new TaskManager(this)
        this.pluginBlockCoordinator = new PluginBlockCoordinator(this, DefaultConnectors)
        this.pluginManager = new PluginsManager(this, this.pluginBlockCoordinator)
        this.inputManager = new InputManager(this)
        this.taskManager.initializeTask(studioConfig)
        this.inputManager.initialize({
            enableViolationUI: this.taskManager.shouldEnableViolations(),
        })
        this.pluginManager.initializePlugins({
            plugins: studioConfig.pluginsConfig,
        })
        this.workbench.enqueueUiUpdate(accessor => {
            accessor.setInitialized(true)
        })
    }

    registerContributorBlocks() {
        builtInBlockComponents.forEach(block => {
            BlockComponentRegistry.getInstance().register(block.type, {
                component: block.component as React.ComponentType<BlockComponentProps<unknown>>,
                schema: block.schema,
            })
        })
    }

    get taskConfig() {
        return this.studioConfig.taskConfig as TaskConfigType<ContainerType, unknown>
    }

    public nextTask = (notifications?: NotificationPropsType) => {
        this.destroy()
        this.studioConfig.onNextTask(notifications)
    }

    public exitTask = (notifications?: NotificationPropsType) => {
        this.destroy()
        this.studioConfig.onExitTask(notifications)
    }

    public destroy = () => {
        this.pluginManager.destroy()
        this.taskManager.destroy()
        this.blockEngine.clear()
        this.taskManager.stopTaskActivityMonitoring()
    }
}
